/**
 * Transforms MarketLab global chart data into format expected by ChartViewTemplate
 * @param {Array} marketData - Array containing header, table, and chart data
 * @returns {Array} - Transformed data for ChartViewTemplate
 */
export const transformGlobalChartData = (marketData) => {
  if (!Array.isArray(marketData)) return [];

  // Find the chart data object
  const chartObj = marketData.find((item) => item.type === "chart");
  if (!chartObj || !chartObj.data) return [];

  // Transform the date-value pairs into chart format
  return Object.entries(chartObj.data)
    .map(([date, value]) => ({
      name: formatDateForChart(date),
      value: Number(value) || 0,
    }))
    .sort((a, b) => new Date(a.name) - new Date(b.name));
};

/**
 * Transforms local chart data (from table cells) into format expected by ChartViewTemplate
 * @param {Object} chartData - Chart data object with display property
 * @returns {Array} - Transformed data for ChartViewTemplate
 */
export const transformLocalChartData = (chartData) => {
  if (!chartData?.display) return [];

  return Object.entries(chartData.display)
    .map(([date, value]) => ({
      name: formatDateForChart(date),
      value: Number(value) || 0,
    }))
    .sort((a, b) => new Date(a.name) - new Date(b.name));
};

/**
 * Formats date string for chart display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date for display
 */
const formatDateForChart = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
};

/**
 * Extracts chart title from MarketLab data
 * @param {Array} marketData - Array containing header, table, and chart data
 * @returns {string} - Chart title
 */
export const getGlobalChartTitle = (marketData) => {
  if (!Array.isArray(marketData)) return "Chart";

  const chartObj = marketData.find((item) => item.type === "chart");
  return chartObj?.widget_name || "Chart";
};

/**
 * Checks if MarketLab data contains chart data
 * @param {Array} marketData - Array containing header, table, and chart data
 * @returns {boolean} - Whether chart data exists
 */
export const hasGlobalChartData = (marketData) => {
  if (!Array.isArray(marketData)) return false;

  const chartObj = marketData.find((item) => item.type === "chart");
  return !!(chartObj && chartObj.data && Object.keys(chartObj.data).length > 0);
};
